import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';

import scrollToTop from '../../../hoc/scrollToTop';

import BooksSubmitButton from '../../../../components/books/common/buttons/BooksSubmitButton';
import BooksLabeledContainer from '../../../../components/books/model1/BooksLabeledContainer';
import BooksMenuItemList from '../../../../components/books/model1/BooksMenuItemList';
import BooksNotes from '../../../../components/books/model1/BooksNotes';
import BooksCheckInput, {
  InputType,
} from '../../../../components/books/model1/forms/BooksCheckInput';
import BooksConfirmAlert from '../../../../components/books/model1/texts/BooksConfirmAlert';
import BooksTermsOfUse from '../../../../components/books/model1/texts/BooksTermsOfUse';
import * as IBooks from '../../../../interfaces/merchants/resources/books';
import * as IInputs from '../../../../interfaces/merchants/resources/books/inputs';
import * as IReserve from '../../../../interfaces/reservations';
import * as $ from './helper';

import {
  inputConfirmation,
  ReinputPaymentError,
  ReselectDateError,
  RestartReservationError,
} from '../../../../redux/actions/merchants/resources/books/inputs';
import { Dispatch } from '../../../../redux/reducers/merchants/resources/books/inputs';

interface ElementProps {
  modelType: IBooks.ModelType;
  merchantResource: IBooks.MerchantResource;
  settings: IBooks.SettingConfirmation;
  postable: boolean;
  onSubmit: () => void;
  onReinputPayment: () => void;
  onReselect: () => void;
  onRestart: () => void;
  publicId: string;
}

interface StateProps {
  inputs: IInputs.State;
}

interface DispatchProps {
  actions: {
    handleSubmit: (
      reservationId: number,
      termsAccepted: boolean | undefined,
      selectedPaymentMethod: IReserve.InputPaymentMethod | undefined,
    ) => Promise<void>;
  };
}
interface State {
  read?: boolean;
}

type Props = ElementProps & StateProps & DispatchProps & InjectedIntlProps;

class InputConfirmation extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const { resourceTerm } = props.settings;
    this.state = { read: resourceTerm ? false : undefined };
    this.handleCheck = this.handleCheck.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  private handleCheck(e: React.ChangeEvent<HTMLInputElement>) {
    const { checked } = e.currentTarget;
    this.setState({ read: checked });
  }

  private async handleSubmit() {
    const {
      inputs,
      onSubmit,
      onRestart,
      onReselect,
      onReinputPayment,
      settings: { selectedPaymentMethod },
    } = this.props;
    const { read } = this.state;
    try {
      await this.props.actions.handleSubmit(
        inputs.reservationId!,
        read,
        selectedPaymentMethod,
      );
      onSubmit();
    } catch (e) {
      if (e instanceof ReselectDateError) {
        onReselect();
      } else if (e instanceof RestartReservationError) {
        onRestart();
      } else if (e instanceof ReinputPaymentError) {
        onReinputPayment();
      }
    }
  }

  public render() {
    const { intl, merchantResource, settings, postable } = this.props;
    const { resourceTerm } = settings;
    const { read } = this.state;
    const reservations = this.props.inputs;
    return (
      <div>
        <BooksConfirmAlert
          termsUrl="/terms"
          lawUrl={`/${this.props.publicId}/legal_notice`}
        />
        <BooksLabeledContainer
          label={intl.formatMessage({
            id: 'books.formsLabel.booksItems',
          })}
          paddingTop={true}
        >
          <BooksMenuItemList
            nickname={this.props.publicId}
            resourceId={merchantResource.publicId}
            resourceName={merchantResource.name}
            resourceImage={
              merchantResource.primaryImage
                ? merchantResource.primaryImage.thumb &&
                  merchantResource.primaryImage.thumb.url
                : ''
            }
            options={reservations.options || []}
            name={reservations.course ? reservations.course.name : ''}
            price={
              reservations.payment &&
              reservations.payment.paymentMethod.method !== 'subscription' &&
              reservations.payment.paymentMethod.method !== 'ticket'
                ? reservations.payment.paymentMethod.price
                : undefined
            }
            payment={
              reservations.payment && reservations.payment.paymentMethod.method
            }
            staff={
              reservations.staff && !$.isRandomStaff(reservations.staff)
                ? reservations.staff.name
                : ''
            }
            courseDate={
              reservations.timeSlotAvailability
                ? reservations.timeSlotAvailability.date
                : ''
            }
            courseTime={
              reservations.timeSlotAvailability
                ? reservations.timeSlotAvailability.value
                : ''
            }
          />
        </BooksLabeledContainer>
        <BooksLabeledContainer
          label={intl.formatMessage({
            id: 'books.formsLabel.attentionForBooking',
          })}
          contentsPadding={true}
          paddingTop={true}
        >
          <BooksNotes notes={$.createNotes(intl, settings)} />
        </BooksLabeledContainer>
        {resourceTerm && (
          <>
            <BooksLabeledContainer
              label={intl.formatMessage({
                id: 'books.formsLabel.termsOfService',
              })}
              contentsPadding={true}
              paddingTop={true}
            >
              <BooksTermsOfUse text={resourceTerm} />
            </BooksLabeledContainer>

            <BooksLabeledContainer
              label={intl.formatMessage({
                id: 'books.formsLabel.needAgree',
              })}
            >
              <BooksCheckInput
                type={InputType.Checkbox}
                onChange={this.handleCheck}
                name="terms-of-use"
                value="agree"
              >
                {intl.formatMessage({
                  id: 'books.forms.agreeTermsOfService',
                })}
              </BooksCheckInput>
            </BooksLabeledContainer>
          </>
        )}
        <BooksSubmitButton
          type="button"
          disabled={(!!resourceTerm && !read) || !postable}
          postable={postable}
          value={intl.formatMessage({
            id: 'books.submitLabel.reserve',
          })}
          onClick={this.handleSubmit}
        >
          {intl.formatMessage({
            id: 'books.submitLabel.reserve',
          })}
        </BooksSubmitButton>
      </div>
    );
  }
}

const mapStateProps = ({
  merchantsResourcesBooksInputs,
}: {
  merchantsResourcesBooksInputs: IInputs.State;
}): StateProps => ({
  inputs: merchantsResourcesBooksInputs,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: {
    handleSubmit: (
      reservationId: number,
      termsAccepted: boolean | undefined,
      selectedPaymentMethod: IReserve.InputPaymentMethod | undefined,
    ) =>
      inputConfirmation(
        reservationId,
        termsAccepted,
        selectedPaymentMethod,
      )(dispatch),
  },
});

export default connect(
  mapStateProps,
  mapDispatchToProps,
)(injectIntl(scrollToTop(InputConfirmation)));
